import { useEffect } from 'react';
import { useConversationRegistryStore } from '@/features/assistant/stores/use-conversation-registry-store';
import type { TabType } from '@/session/types';

/**
 * Writes focusedConversationId to the registry store from the workspace's
 * focused panel. Called once per WorkspaceView render (one per panel).
 *
 * Rules:
 *   - When isFocused && tabType === 'conversation': set focused to activeId.
 *   - When isFocused && tabType !== 'conversation': clear focused to null
 *     (user is on a doc/canvas/etc. tab — no conversation is focused).
 *   - When !isFocused: do nothing. Only the focused panel owns the signal.
 *     The non-focused panel must not clobber the value written by the focused
 *     panel (e.g. split view where one panel shows a doc, the other a
 *     running conversation that we must NOT badge).
 */
export function useTrackFocusedConversation({
  isFocused,
  activeId,
  tabType,
}: {
  isFocused: boolean;
  activeId: string;
  tabType: TabType;
}): void {
  useEffect(() => {
    if (!isFocused) return;
    const { setFocused } = useConversationRegistryStore.getState();
    if (tabType === 'conversation') {
      setFocused(activeId);
    } else {
      setFocused(null);
    }
    return () => {
      // Clear focus on unmount (or dep change) only when this effect-run had
      // set a specific conversation id. Guard: only null out if the store still
      // holds this conversation's id — another panel may have taken focus in the
      // meantime and we must not clobber its value.
      if (
        tabType === 'conversation' &&
        useConversationRegistryStore.getState().focusedConversationId === activeId
      ) {
        useConversationRegistryStore.getState().setFocused(null);
      }
    };
  }, [isFocused, activeId, tabType]);
}
